/**
 * emailService.ts
 *
 * Client wrapper for the sendMagicLinkEmail Cloud Function.
 * The function lives in /functions/src/email/sendMagicLink.ts and
 * actually performs the Resend API call — we never expose the
 * Resend API key to the browser.
 *
 * Usage:
 *   const result = await sendMagicLinkEmail({ tokenId });
 *   if (result.status === 'sent') toast.show('Email sent.');
 */

import { getFunctions, httpsCallable, type HttpsCallable } from 'firebase/functions';

export interface SendMagicLinkInput {
  tokenId: string;
  /** Optional override of the recipient email address (test sends). */
  toOverride?: string;
  /** Re-send even when already sent. */
  force?: boolean;
}

export interface SendMagicLinkResult {
  status: 'sent' | 'already_sent';
  sentAt: string;                  // ISO
  providerMessageId?: string;
  to: string;
}

let _callable: HttpsCallable<SendMagicLinkInput, SendMagicLinkResult> | null = null;
function getCallable(): HttpsCallable<SendMagicLinkInput, SendMagicLinkResult> {
  if (!_callable) {
    const functions = getFunctions(undefined, 'us-central1');
    _callable = httpsCallable<SendMagicLinkInput, SendMagicLinkResult>(
      functions,
      'sendMagicLinkEmail',
    );
  }
  return _callable;
}

/**
 * Send the magic-link email for one token. Idempotent — repeated
 * calls return `{ status: 'already_sent' }` unless `force: true`.
 */
export async function sendMagicLinkEmail(
  input: SendMagicLinkInput,
): Promise<SendMagicLinkResult> {
  const callable = getCallable();
  const res = await callable(input);
  return res.data;
}

/**
 * Bulk-send a batch of token ids. Per-token errors are caught so a
 * single failure doesn't abort the batch — caller receives an array
 * with one entry per input.
 */
export interface BulkSendResult {
  tokenId: string;
  ok: boolean;
  status?: SendMagicLinkResult['status'];
  to?: string;
  error?: string;
}

export async function sendMagicLinkEmailsBulk(
  tokenIds: string[],
  opts?: { force?: boolean },
): Promise<BulkSendResult[]> {
  return Promise.all(
    tokenIds.map(async (tokenId): Promise<BulkSendResult> => {
      try {
        const r = await sendMagicLinkEmail({ tokenId, force: opts?.force });
        return { tokenId, ok: true, status: r.status, to: r.to };
      } catch (e) {
        return { tokenId, ok: false, error: (e as Error).message };
      }
    }),
  );
}
